import { useGetStoreAvailabilityFn, useIsMobileOrderingAvailable } from 'features/store/hooks';
import { useStorePosConnectivityStatus } from 'features/store/hooks/use-store-connectivity-status';
import { milesBetweenCoordinates, readableDistanceFromStore } from 'features/store/utils';
import { useIntl } from 'react-intl';

import { IRestaurant } from '@rbi-ctg/store';
import { ServiceMode } from 'generated/graphql-gateway';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { getStoreSEOSlug } from 'utils/slugify';

import { useStoreAvailabilityFns } from './use-store-availability-fns';

interface IUseStoreCardOptions {
  restaurant: IRestaurant;
  shouldGetRemoteAvailabilityData?: boolean;
}

export function useStoreCard({
  restaurant,
  shouldGetRemoteAvailabilityData = true,
}: IUseStoreCardOptions) {
  const { activeCoordinates } = useGeolocation();
  const { region } = useLocale();
  const { formatMessage } = useIntl();
  const { isServiceModeAvailable } = useStoreAvailabilityFns();

  const driveThruOpen = isServiceModeAvailable(restaurant, ServiceMode.DRIVE_THRU);
  const mobileOrderDriveThruOpen = isServiceModeAvailable(
    restaurant,
    ServiceMode.MOBILE_ORDER_DRIVE_THRU
  );
  const dineInOpen = isServiceModeAvailable(restaurant, ServiceMode.EAT_IN);
  const takeoutOpen = isServiceModeAvailable(restaurant, ServiceMode.TAKEOUT);
  const diningRoomOpen = dineInOpen || takeoutOpen;
  const curbsideOpen = isServiceModeAvailable(restaurant, ServiceMode.CURBSIDE);
  const openHours = driveThruOpen ? restaurant.driveThruHours : restaurant.diningRoomHours;
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const areDiagnosticToolsEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const checkAvailability = useGetStoreAvailabilityFn();

  const restaurantConnectivityStatus = useStorePosConnectivityStatus({
    // @ts-expect-error TS(2322) FIXME: Type 'Maybe<string> | undefined' is not assignable... Remove this comment to see the full error message
    storeId: restaurant.number,
    skip: !shouldGetRemoteAvailabilityData,
  });

  // use the just-fetched restaurant if queried from backend
  const { isRestaurantPosOnline } = shouldGetRemoteAvailabilityData
    ? restaurantConnectivityStatus
    : {
        isRestaurantPosOnline: checkAvailability(restaurant),
      };

  let distanceText;
  if (activeCoordinates && restaurant.latitude && restaurant.longitude) {
    const distance = milesBetweenCoordinates(activeCoordinates, {
      lat: restaurant.latitude,
      lng: restaurant.longitude,
    });
    distanceText = readableDistanceFromStore(distance, region, formatMessage);
  }

  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);

  const storeSEOSlug = getStoreSEOSlug(restaurant);

  return {
    storeSEOSlug,
    distanceText,
    curbsideOpen,
    dineInOpen,
    takeoutOpen,
    diningRoomOpen,
    driveThruOpen,
    mobileOrderDriveThruOpen,
    openHours,
    isMobileOrderingAvailable,
    isRestaurantPosOnline: !!isRestaurantPosOnline,
    enableOrdering,
    areDiagnosticToolsEnabled,
  };
}
